import { ApplicationConfig, inject, isDevMode, LOCALE_ID } from '@angular/core';
import {
  provideRouter,
  RouteReuseStrategy,
  withComponentInputBinding,
  withHashLocation,
  withRouterConfig,
} from '@angular/router';

import { CurrencyPipe, registerLocaleData } from '@angular/common';
import { HttpBackend, provideHttpClient, withInterceptors } from '@angular/common/http';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { BASE_CONFIG, BaseConfig } from '@libs/core/base-config';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';

import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { clientInfoInterceptor } from '@libs/mtjr/shared/http/client-info-interceptor';
import { addIcons } from 'ionicons';
import {
  addCircle,
  addCircleOutline,
  arrowBack,
  caretDownSharp,
  cartOutline,
  checkmark,
  checkmarkCircle,
  checkmarkCircleOutline,
  chevronExpand,
  chevronForwardOutline,
  close,
  closeCircle,
  closeCircleOutline,
  copyOutline,
  ellipsisVertical,
  gridOutline,
  image,
  imageOutline,
  informationCircleOutline,
  logoFacebook,
  logoLinkedin,
  logoTwitter,
  logoWhatsapp,
  notificationsOutline,
  refresh,
  removeCircleOutline,
  shareOutline,
  shareSocialOutline,
  shieldCheckmarkOutline,
  trash,
  trashBinOutline,
  trashOutline,
} from 'ionicons/icons';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';

import localeAr from '@angular/common/locales/ar';
import localeEn from '@angular/common/locales/en';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, Messaging, provideMessaging } from '@angular/fire/messaging';
import { provideServiceWorker } from '@angular/service-worker';
import { routes } from '@apps/mtjr/customers/app/app.routes';
import { CartStore } from '@apps/mtjr/customers/app/cart/cart.store';
import { environment } from '@apps/mtjr/customers/environments/environment';
import { Capacitor } from '@capacitor/core';
import { AuthStorageService } from '@libs/core/lib/auth-storage-service';
import { buildAngularAuthConfig, UserScope } from '@libs/core/lib/auth/logto';
import { ADDRESS_PROVIDER } from '@libs/mtjr/shared/address/address-provider.token';
import {
  AbstractSecurityStorage,
  authInterceptor,
  provideAuth,
  withAppInitializerAuthCheck,
} from 'angular-auth-oidc-client';
registerLocaleData(localeAr);
registerLocaleData(localeEn);

const isNative = Capacitor.isNativePlatform();

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withComponentInputBinding(),
      withHashLocation(),
      // withDebugTracing(),
    ),
    provideIonicAngular({
      mode: 'ios',
    }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },

    provideAuth(
      {
        config: {
          ignoreNonceAfterRefresh: true,
          secureRoutes: [`${environment.baseUrl}/api`],
          ...buildAngularAuthConfig({
            endpoint: 'https://auth.mtjr.ly/',
            // appId: '8ii07hi2lm2fsst4tkf2v',
            appId: isNative ? 'lkh982hxgwtgyf1kbob3p' : '8ii07hi2lm2fsst4tkf2v',
            redirectUri: environment.auth.redirectUri,
            postLogoutRedirectUri: environment.auth.postLogoutRedirectUri,
            resource: environment.auth.resource,
            includeReservedScopes: true,
            scopes: [UserScope.Email, UserScope.Phone, UserScope.CustomData],
          }),
          renewTimeBeforeTokenExpiresInSeconds: 180,
          silentRenew: true,
          disableCleaningPopupOnInvalidMessage: true,
        },
      },
      withAppInitializerAuthCheck(),
    ),
    { provide: AbstractSecurityStorage, useClass: AuthStorageService },

    provideHttpClient(withInterceptors([authInterceptor(), clientInfoInterceptor])),
    { provide: LOCALE_ID, useValue: 'en' },
    {
      provide: BASE_CONFIG,
      useValue: {
        appName: 'mtjr Customers',
        flavor: 'customers',
        baseUrl: environment.baseUrl,
        logo: 'https://nbg1.your-objectstorage.com/mtjr/logo.svg',
        loginTitle: 'Welcome to mtjr',
        // todo: change subtitle
        loginSubtitle: 'Customer portal.',
        logoHeight: '100',
        shareBaseUrl: environment.shareBaseUrl,
        authRedirectUri: environment.auth.redirectUri,
      } satisfies BaseConfig,
    },

    CurrencyPipe,
    NgEventBus,
    {
      provide: ADDRESS_PROVIDER,
      useFactory: () => {
        const cartStore = inject(CartStore);
        return { address: cartStore.address };
      },
    },
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpBackend) =>
          new MultiTranslateHttpLoader(http, [
            { prefix: '/i18n/', suffix: '/manual.json' },
            { prefix: '/i18n/', suffix: '/generated.json' },
          ]),
        deps: [HttpBackend],
      },
      lang: localStorage.getItem('lang') || 'ar',
      fallbackLang: 'ar',
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      // enabled: true,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideFirebaseApp(() =>
      initializeApp({
        projectId: 'mtjrly',
        appId: '1:963602624681:web:49ab60525f2380b04db9b8',
        storageBucket: 'mtjrly.firebasestorage.app',
        apiKey: 'AIzaSyBC1_5RL1db3_AWRa-kKNiikdMmPMjeN8c',
        authDomain: 'mtjrly.firebaseapp.com',
        messagingSenderId: '963602624681',
      }),
    ),
    provideMessaging(() => {
      // Don't provide Firebase Messaging in Capacitor native apps
      if (Capacitor.isNativePlatform()) {
        return null as unknown as Messaging;
      }

      // Only provide messaging for web/HTTPS
      if (
        typeof window !== 'undefined' &&
        (window.location.protocol === 'https:' || window.location.hostname === 'localhost')
      ) {
        return getMessaging();
      }

      return {} as Messaging;
    }),
  ],
};

addIcons({
  shareIos: 'share-ios.svg',
  usdt: 'svgs/usdt.svg',
  usdc: 'svgs/usdc.svg',
  wPay: 'svgs/w-pay.svg',
  moamalat: 'svgs/moamalat.svg',
  moamalatFull: 'svgs/moamalat-full.svg',
  yussorPay: 'svgs/yussor-pay.svg',
  masrafyPay: 'svgs/masrafy-pay.svg',
  star: 'assets/icons/star.svg',
  starFilled: 'assets/icons/star-filled.svg',
  starCircle: 'assets/icons/star-circle.svg',
  locationAdd: 'assets/icons/location-add.svg',
  heartFilled: 'assets/icons/heart-filled.svg',
  heart: 'assets/icons/heart.svg',
  filter: 'assets/icons/filter.svg',
  cart: 'assets/icons/cart.svg',
  home: 'assets/icons/home.svg',
  person: 'assets/icons/user.svg',
  key: 'assets/icons/key-square.svg',
  logout: 'assets/icons/logout.svg',
  more: 'assets/icons/more.svg',
  location: 'assets/icons/location.svg',
  theme: 'assets/icons/theme.svg',
  orders: 'assets/icons/orders.svg',
  moreVer: 'assets/icons/more-ver.svg',
  messages: 'assets/icons/messages.svg',
  send: 'assets/icons/send.svg',
  cash: 'assets/icons/money.svg',
  crypto: 'assets/icons/crypto.svg',
  language: 'assets/icons/language.svg',
  wallet: 'assets/icons/wallet.svg',
  moneyIn: 'assets/icons/money-in.svg',
  moneyOut: 'assets/icons/money-out.svg',
  topUp: 'assets/icons/top-up.svg',
  settings: 'assets/icons/settings.svg',
  addSquare: 'assets/icons/add-square.svg',
  infoCircle: 'assets/icons/info-circle.svg',
  refund: 'assets/icons/refund.svg',
  lock: 'assets/icons/lock.svg',
  release: 'assets/icons/release.svg',
  hold: 'assets/icons/hold.svg',
  myLocation: 'assets/icons/my-location.svg',
  refresh,
  trashBinOutline,
  addCircleOutline,
  removeCircleOutline,
  imageOutline,
  checkmarkCircle,
  caretDownSharp,
  gridOutline,
  addCircle,
  checkmarkCircleOutline,
  trash,
  image,
  chevronExpand,
  closeCircle,
  informationCircleOutline,
  closeCircleOutline,
  checkmark,
  close,
  copyOutline,
  trashOutline,
  notificationsOutline,
  shareSocialOutline,
  shareOutline,
  logoFacebook,
  logoTwitter,
  logoWhatsapp,
  logoLinkedin,
  arrowBack,
  cartOutline,
  ellipsisVertical,
  shieldCheckmarkOutline,
  chevronForwardOutline,
});
